import React, { useEffect, useState } from "react";
import { useAppContext } from "../context/AppContext";
import {
  getAllLanguages,
  getBrandDetails,
  getDivisionsWithBrand,
  getModels,
  setDefaultValues,
  createCustomTov,
  updateCustomTov,
} from "../services/app.service";
import { PERSONA_INITIAL_OBJECT } from "../utils/settings";
import { useNavigate } from "react-router-dom";
import icon_loading from "../../../assets/elysia-loader.gif";
import IconTick from "../../../assets/icon-google-tick.svg";
import IconAngleDown from "../../../assets/icon-arrow-down.svg";
import CustomToneOfVoice from "./CustomToneOfVoice";
import DeleteToneOfVoice from "./DeleteToneOfVoice";
import { showApiErrorToast } from "./notify";
import { encodeBase64 } from "../utils/chat";
import useToneOfVoice from "../utils/hooks/useToneOfVoice";

const EMPTY_BRAND: Brand = { id: "", name: "" };
const EMPTY_MODEL = { name_of_model: "", display_name: "", model: "" };

const Settings = () => {
  const [tempSelectedModel, setTempSelectedModel] = useState(EMPTY_MODEL);
  const [tempSelectedLanguage, setTempSelectedLanguage] = useState("");
  const [tempSelectedDivision, setTempSelectedDivision] = useState("");
  const [tempSelectedBrand, setTempSelectedBrand] = useState<Brand>(EMPTY_BRAND);
  const [tempSelectedPersona, setTempSelectedPersona] = useState<Persona>(PERSONA_INITIAL_OBJECT);
  const [allPersonas, setAllPersonas] = useState<any>([]);

  const [allModels, setAllModels] = useState<Model[]>([]);
  const [allBrands, setAllBrands] = useState<Brand[]>([]);
  const [allLanguages, setAllLanguages] = useState<string[]>([]);
  const [allDivisions, setAllDivisions] = useState([]);
  const [tempSelectedTovId, setTempSelectedTovId] = useState("");
  const [loadingOnSave, setLoadingOnSave] = useState(false);
  const [showSavedBadge, setShowSavedBadge] = useState(false);
  const [canSave, setCanSave] = useState(true);
  const [showError, setShowError] = useState(false);

  // LOCAL, unsaved UI toggle
  const [tovOption, setTovOption] = useState("");
  const {
    showTovForm,
    setShowTovForm,
    showDeleteView,
    setShowDeleteView,
    typeSelection,
    setTypeSelection,
    tovFormMode,
    setTovFormMode,
    exceedsWordLimit,
    setExceedsWordLimit,
    defaultToneOfVoice,
    selectedModel,
    selectedLanguage,
    selectedDivision,
    selectedBrand,
    selectedPersona,
    setSelectedModel,
    setSelectedLanguage,
    setSelectedDivision,
    setSelectedBrand,
    setSelectedPersona,
    setSelectedTovId,
    setModelProvider,
  } = useAppContext();

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Everything about Custom Tone of Voice (fetch, form fields, delete) now
  // lives in useCustomToneOfVoice.tsx — logic is identical, only moved.
  const {
    customTovs,
    setCustomTovs,
    selectedTovName,
    setSelectedTovName,
    customName,
    setCustomName,
    instruction,
    setInstruction,
    selectedTovObj,
    hasTovFormChanged,
    handleDelete,
  } = useToneOfVoice({
    tovFormMode,
    EMPTY_BRAND,
    setLoadingOnSave,
    setShowDeleteView,
    setShowTovForm,
    setTovFormMode,
    setTypeSelection,
    setTovOption,
    setTempSelectedDivision,
    setTempSelectedBrand,
    setTempSelectedPersona,
    setSelectedTovId,
  });

  useEffect(() => {
    const allModels = async () => {
      setIsLoading(true);
      const response = await getModels();
      setAllModels(response ?? []);
      setIsLoading(false);
    };
    const allLanguages = async () => {
      const response = await getAllLanguages();
      setAllLanguages(response);
    };
    allModels();
    allLanguages();
  }, []);

  useEffect(() => {
    const getDivisionAndBrands = async () => {
      const response = await getDivisionsWithBrand();
      setAllDivisions?.(response);
    };
    getDivisionAndBrands();
  }, []);

  useEffect(() => {
    setTempSelectedLanguage(selectedLanguage);
    setTempSelectedDivision(selectedDivision);
    setTempSelectedBrand(selectedBrand ?? EMPTY_BRAND);
    setTempSelectedPersona(selectedPersona);
    setTovOption(typeSelection); // seed local UI toggle from last-saved context value
  }, []);

  useEffect(() => {
    if (!selectedModel || !allModels?.length) return;

    const defaultModel = allModels.find((model) => model.name_of_model === selectedModel);

    if (defaultModel) {
      setTempSelectedModel({
        name_of_model: defaultModel.name_of_model,
        display_name: defaultModel.display_name,
        model: defaultModel.model,
      });
    }
  }, [selectedModel, allModels]);

  useEffect(() => {
    if (!allDivisions?.length) return;
    setAllBrands(allDivisions?.find((div) => div.division === tempSelectedDivision)?.brand ?? []);
  }, [tempSelectedDivision, allDivisions]);

  useEffect(() => {
    const fetchPersonas = async () => {
      const res = await getBrandDetails(tempSelectedBrand.id);
      if (res) {
        setAllPersonas(res?.communication?.target_audiences);
        setTempSelectedTovId(res?.id);
        setCanSave(true);
        setShowError(false);
      } else {
        setShowError(true);
        setCanSave(false);
      }
    };
    if (tempSelectedBrand?.id?.length) {
      fetchPersonas();
    }
  }, [tempSelectedBrand]);

  const handleModelSelect = (e) => {
    const modelDetails = allModels?.find((model) => model.display_name === e.target.value);
    setTempSelectedModel({
      name_of_model: modelDetails?.name_of_model,
      display_name: modelDetails?.display_name,
      model: modelDetails?.model,
    });
  };

  const handleLanguageSelect = (e: any) => {
    const value = e.target.value;
    setTempSelectedLanguage(value);
  };

  const handleDivisionSelect = (e: any) => {
    const value = e.target.value;
    setTempSelectedDivision(value);
    setTempSelectedBrand(EMPTY_BRAND);
    setTempSelectedPersona(PERSONA_INITIAL_OBJECT);
    setAllPersonas([]);
    setCanSave(false);
    setShowError(false);
  };

  // Functions for TOV
  const handleTypeSelection = (e: any) => {
    const value = e.target.value;
    setTovOption(value);

    if (value === "brand" && Object.keys(defaultToneOfVoice).length > 0) {
      setTempSelectedDivision(defaultToneOfVoice?.division ?? "");
      setTempSelectedBrand(defaultToneOfVoice?.brand ?? EMPTY_BRAND);
      setTempSelectedPersona(defaultToneOfVoice?.persona ?? PERSONA_INITIAL_OBJECT);
    }
  };

  const handleBrandSelect = (e: any) => {
    setAllPersonas([]);
    const brands = allDivisions?.find((div) => div.division === tempSelectedDivision)?.brand ?? [];
    setTempSelectedBrand(brands.find((brand) => brand.id === e.target.value) ?? EMPTY_BRAND);
    setTempSelectedPersona(PERSONA_INITIAL_OBJECT);
  };

  const handlePersonaSelect = (e) => {
    setTempSelectedPersona(
      allPersonas?.find((persona) => persona.id === e.target.value) ?? PERSONA_INITIAL_OBJECT
    );
  };

  const handleCancel = () => {
    if (showDeleteView) {
      // Delete view
      setShowDeleteView(false);
    } else if (showTovForm) {
      // ToV form
      setShowTovForm(false);
      setTovFormMode("");
      setExceedsWordLimit(false);
    } else {
      setTovFormMode("");
      setTovOption(typeSelection);
      navigate("/");
    }
  };

  const handleSave = async () => {
    // active tone of voice id to call edit and save in single go
    let activeTovId;
    try {
      setLoadingOnSave(true);
      const tovIdToSave = tovOption === "custom" ? selectedTovObj?.id : tempSelectedTovId;
      const modelName =
        tempSelectedModel?.name_of_model?.length > 0
          ? tempSelectedModel?.name_of_model
          : selectedModel;
      if (showTovForm) {
        if (tovFormMode === "edit") {
          await updateCustomTov(
            selectedTovObj?.id,
            customName ? encodeBase64(customName) : encodeBase64(selectedTovObj.name),
            instruction ? encodeBase64(instruction) : encodeBase64(selectedTovObj?.instruction)
          );
          setCustomTovs((prev) =>
            prev.map((tov) =>
              tov.name === selectedTovName ? { ...tov, name: customName, instruction } : tov
            )
          );
          setSelectedTovName(customName);
          setTovFormMode("");
        } else {
          const res = await createCustomTov(encodeBase64(customName), encodeBase64(instruction));
          const newId = res?.data?.data?.id;
          setCustomTovs((prev) => [...prev, { id: newId, name: customName, instruction }]);
          setSelectedTovName(customName);
          activeTovId = newId;
        }
        const resolvedTovId = tovIdToSave && tovIdToSave.length > 0 ? tovIdToSave : activeTovId;
        await setDefaultValues(
          modelName,
          tempSelectedLanguage,
          resolvedTovId,
          tempSelectedPersona.id,
          tovOption.toLowerCase()
        );
        setSelectedTovId(resolvedTovId);
        setTypeSelection(tovOption);
        setShowTovForm(false);
      } else {
        await setDefaultValues(
          modelName,
          tempSelectedLanguage,
          tovIdToSave,
          tempSelectedPersona.id,
          tovOption.toLowerCase()
        );
        setTypeSelection(tovOption);
        setSelectedModel(tempSelectedModel.name_of_model);
        setSelectedLanguage(tempSelectedLanguage);
        setSelectedTovId(tovIdToSave);
        setModelProvider(tempSelectedModel.model);
        if (tovOption === "brand") {
          setSelectedDivision(tempSelectedDivision);
          setSelectedBrand(tempSelectedBrand ?? EMPTY_BRAND);
          setSelectedPersona(tempSelectedPersona);
        }
        setShowSavedBadge(true);
        setTimeout(() => {
          setShowSavedBadge(false);
        }, 1000);
      }
    } catch (error) {
      console.error(error);
      showApiErrorToast("Something went wrong.");
    } finally {
      setLoadingOnSave(false);
    }
  };

  return (
    <div className="settings-wrapper">
      <div className={`settings-container ${isLoading ? "loading" : ""}`}>
        <div className="settings-container">
          <div className="settings-sub-headings-container">
            {showDeleteView ? (
              <DeleteToneOfVoice />
            ) : showTovForm ? (
              <CustomToneOfVoice
                setInstruction={setInstruction}
                setCustomName={setCustomName}
                initialName={customName}
                initialInstruction={instruction}
                tovFormMode={tovFormMode}
                setExceedsWordLimit={setExceedsWordLimit}
              />
            ) : (
              <>
                <div className="settings-sub-headings">
                  <div className="settings-heading">LLM Model</div>

                  <label className="select-label" htmlFor="llm-model">
                    Select LLM Model*
                  </label>

                  <div className="select-wrap">
                    <select
                      id="llm-model"
                      className="settings-dropdowns"
                      value={tempSelectedModel.display_name}
                      onChange={handleModelSelect}
                      aria-label="Select LLM Model"
                    >
                      {allModels?.map((model, idx) => (
                        <option key={idx} value={model.display_name}>
                          {model.display_name}
                        </option>
                      ))}
                    </select>
                    <IconAngleDown />
                  </div>
                </div>
                <hr className="settings-divider" />
                <div className="settings-sub-headings">
                  <div className="settings-heading">Language</div>

                  <label className="select-label" htmlFor="Select Language">
                    Select Language*
                  </label>

                  <div className="select-wrap">
                    <select
                      id="Select Language"
                      className="settings-dropdowns"
                      value={tempSelectedLanguage}
                      onChange={handleLanguageSelect}
                      aria-label="Select Language"
                    >
                      {allLanguages.map((language, idx) => (
                        <option key={idx} value={language}>
                          {language}
                        </option>
                      ))}
                    </select>
                    <IconAngleDown />
                  </div>
                </div>
                <hr className="settings-divider" />
                <div className="settings-sub-headings">
                  <div className="settings-heading">Tone Of Voice</div>
                  {/* Type addition */}
                  <div>
                    <label className="select-label" htmlFor="type-select">
                      Type*
                    </label>
                    <div className="select-wrap">
                      <select
                        id="type-select"
                        className="settings-dropdowns"
                        value={tovOption}
                        onChange={handleTypeSelection}
                        aria-label="Type selection"
                      >
                        <option value="brand">Brand</option>
                        <option value="custom">Custom</option>
                      </select>
                      <IconAngleDown />
                    </div>
                  </div>
                  {tovOption == "brand" && (
                    <>
                      <label className="select-label" htmlFor="Division">
                        Division*
                      </label>
                      <div className="select-wrap">
                        <select
                          id="Division"
                          className="settings-dropdowns"
                          value={tempSelectedDivision}
                          onChange={handleDivisionSelect}
                          aria-label="Division"
                        >
                          {allDivisions?.map((division, idx) => (
                            <option key={idx} value={division.division}>
                              {division.division}
                            </option>
                          ))}
                        </select>
                        <IconAngleDown />
                      </div>
                      <label className="select-label" htmlFor="Brand">
                        Brand*
                      </label>
                      {showError && (
                        <div className="error-msg">
                          Sorry no tone of voice created for the selected brand
                        </div>
                      )}
                      <div className="select-wrap">
                        <select
                          id="Brand"
                          className="settings-dropdowns"
                          value={tempSelectedBrand.id}
                          onChange={handleBrandSelect}
                          aria-label="Brand"
                        >
                          <option value="">Please select a brand</option>
                          {allDivisions
                            ?.find((div) => div.division === tempSelectedDivision)
                            ?.brand.map((brand) => (
                              <option key={brand.id} value={brand.id}>
                                {brand.name}
                              </option>
                            ))}
                        </select>
                        <IconAngleDown />
                      </div>
                      <label className="select-label" htmlFor="Persona">
                        Persona
                      </label>
                      <div className="select-wrap">
                        <select
                          id="Persona"
                          className="settings-dropdowns"
                          value={tempSelectedPersona?.id}
                          onChange={handlePersonaSelect}
                          aria-label="Persona"
                          disabled={!allPersonas?.length}
                        >
                          <option value="">Please select an optional persona</option>
                          {allPersonas?.length &&
                            allPersonas?.map((persona: any) => (
                              <option key={persona?.id} value={persona?.id}>
                                {persona?.name}
                              </option>
                            ))}
                        </select>
                        <IconAngleDown />
                      </div>
                    </>
                  )}
                  {tovOption === "custom" && (
                    <>
                      {customTovs.length > 0 ? (
                        <>
                          <div>
                            <label className="select-label" htmlFor="tov-name-select">
                              Name*
                            </label>
                          </div>
                          <div className="select-wrap">
                            {customTovs[0] && (
                              <div className="tov-name-display">{customTovs[0].name}</div>
                            )}
                          </div>
                          <button
                            className="add-tov-btn"
                            onClick={() => {
                              setTovFormMode("edit");
                              setCustomName(selectedTovObj?.name ?? "");
                              setInstruction(selectedTovObj?.instruction ?? "");
                              setShowTovForm(true);
                            }}
                          >
                            <label className="tone-of-voice">Edit tone of voice</label>
                          </button>
                        </>
                      ) : (
                        <>
                          <p className="instruction-info">
                            No custom Tone of Voice has been added yet. Add one to get started.
                          </p>
                          <button
                            className="add-tov-btn"
                            onClick={() => {
                              setTovFormMode("add");
                              setCustomName("");
                              setInstruction("");
                              setShowTovForm(true);
                            }}
                          >
                            <label className="tone-of-voice">Add new tone of voice</label>
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {showSavedBadge && (
            <span className="badge-for-save-button">
              <IconTick />
              <span>Saved</span>
            </span>
          )}

          <div className="settings-buttons">
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>

            {showDeleteView ? (
              // Delete view — confirm delete
              <button className="delete-btn" onClick={handleDelete}>
                <span>{loadingOnSave ? <img src={icon_loading} alt="loading" /> : "Delete"}</span>
              </button>
            ) : tovOption === "custom" ? (
              // Custom ToV — delete + save
              <div className="edit-tov-mode">
                {showTovForm && tovFormMode === "edit" && (
                  <button className="delete-btn" onClick={() => setShowDeleteView(true)}>
                    <div className="btn-content">Delete</div>
                  </button>
                )}
                <button
                  className="save-btn"
                  disabled={
                    exceedsWordLimit ||
                    (tovFormMode !== "" ? !hasTovFormChanged : !selectedTovObj?.id)
                  }
                  onClick={handleSave}
                >
                  <div className="btn-content">
                    {loadingOnSave ? <img src={icon_loading} alt="loading" /> : "Save"}
                  </div>
                </button>
              </div>
            ) : (
              // Brand — save only
              <button
                className="save-btn"
                disabled={!canSave || tempSelectedBrand.id === ""}
                onClick={handleSave}
              >
                <div className="btn-content">
                  {loadingOnSave ? <img src={icon_loading} alt="loading" /> : "Save"}
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
      {isLoading && (
        <div className="settings-loading-overlay">
          <img src={icon_loading} alt="saving..." />
        </div>
      )}
    </div>
  );
};

export default Settings;
